import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { Megaphone, Save, AlertCircle, CheckCircle, RotateCcw, Sparkles, X, Plus, Trash2 } from 'lucide-react';
import PromoCouponChips from './PromoCouponChips';
import { db, auth } from '../firebase';
import {
  PUBLIC_SITE_CONTENT_COLLECTION,
  PROMO_BANNER_DOC_ID,
  PROMO_BANNER_VARIANTS,
  PROMO_BANNER_MAX_COUPON_CODES,
  DEFAULT_PROMO_BANNER,
  VARIANT_STYLES,
  resolvePromoBanner,
} from '../constants/promoBanner';

const VARIANT_LABELS = {
  teal: 'Brand teal',
  amber: 'Amber (urgency)',
  emerald: 'Emerald',
  slate: 'Slate (subtle)',
};

/**
 * Admin/superuser editor for the site-wide promotional banner. Writes to
 * publicSiteContent/promoBanner — the same doc the PromoBanner component
 * subscribes to on every page.
 *
 * Saving auto-bumps `version`, which acts as a cache-buster for the
 * per-user dismissal stored in localStorage. So editing the message will
 * also re-show the banner to anyone who'd previously closed it.
 */
export default function PromoBannerEditor() {
  const [enabled, setEnabled] = useState(DEFAULT_PROMO_BANNER.enabled);
  const [message, setMessage] = useState(DEFAULT_PROMO_BANNER.message);
  const [couponCodes, setCouponCodes] = useState(['']);
  const [ctaLabel, setCtaLabel] = useState(DEFAULT_PROMO_BANNER.ctaLabel);
  const [ctaUrl, setCtaUrl] = useState(DEFAULT_PROMO_BANNER.ctaUrl);
  const [variant, setVariant] = useState(DEFAULT_PROMO_BANNER.variant);
  const [version, setVersion] = useState(DEFAULT_PROMO_BANNER.version);
  const [bumpVersionOnSave, setBumpVersionOnSave] = useState(true);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message_, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const ref = doc(db, PUBLIC_SITE_CONTENT_COLLECTION, PROMO_BANNER_DOC_ID);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : null;
        if (cancelled) return;
        const r = resolvePromoBanner(data);
        setEnabled(r.enabled);
        setMessage(r.message);
        setCouponCodes(r.couponCodes.length > 0 ? r.couponCodes : ['']);
        setCtaLabel(r.ctaLabel);
        setCtaUrl(r.ctaUrl);
        setVariant(r.variant);
        setVersion(r.version);
      } catch (e) {
        console.error('PromoBannerEditor load error:', e);
        if (!cancelled) setError(e.message || 'Failed to load promo banner.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    setError(null);
    const nextVersion = bumpVersionOnSave ? Math.max(1, Math.floor(version || 0)) + 1 : version;
    const codesToSave = couponCodes
      .map((c) => (typeof c === 'string' ? c.trim().toUpperCase() : ''))
      .filter(Boolean)
      .slice(0, PROMO_BANNER_MAX_COUPON_CODES);
    try {
      await setDoc(
        ref,
        {
          enabled,
          message: (message || '').trim(),
          couponCodes: codesToSave,
          couponCode: deleteField(),
          ctaLabel: (ctaLabel || '').trim() || DEFAULT_PROMO_BANNER.ctaLabel,
          ctaUrl: (ctaUrl || '').trim() || DEFAULT_PROMO_BANNER.ctaUrl,
          variant,
          version: nextVersion,
          updatedAt: serverTimestamp(),
          updatedByEmail: auth.currentUser?.email || null,
        },
        { merge: true }
      );
      setVersion(nextVersion);
      setStatus(
        bumpVersionOnSave
          ? 'Saved. Banner version bumped — anyone who previously dismissed it will see the new one again.'
          : 'Saved. Existing dismissals were kept (version not bumped).'
      );
    } catch (e) {
      console.error('PromoBannerEditor save error:', e);
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setEnabled(DEFAULT_PROMO_BANNER.enabled);
    setMessage(DEFAULT_PROMO_BANNER.message);
    setCouponCodes(['']);
    setCtaLabel(DEFAULT_PROMO_BANNER.ctaLabel);
    setCtaUrl(DEFAULT_PROMO_BANNER.ctaUrl);
    setVariant(DEFAULT_PROMO_BANNER.variant);
    setStatus('Reset to defaults — click Save to apply.');
  };

  if (loading) {
    return <div className="text-sm text-slate-500 py-8">Loading promo banner…</div>;
  }

  const previewStyles = VARIANT_STYLES[variant] || VARIANT_STYLES.teal;
  const previewCouponCodes = couponCodes
    .map((c) => (typeof c === 'string' ? c.trim().toUpperCase() : ''))
    .filter(Boolean);

  const updateCouponAt = (index, value) => {
    setCouponCodes((prev) => prev.map((c, i) => (i === index ? value.toUpperCase() : c)));
  };

  const addCouponRow = () => {
    setCouponCodes((prev) => {
      if (prev.length >= PROMO_BANNER_MAX_COUPON_CODES) return prev;
      return [...prev, ''];
    });
  };

  const removeCouponRow = (index) => {
    setCouponCodes((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [''];
    });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            <Megaphone className="text-slate-600 shrink-0 mt-0.5" size={22} />
            <div>
              <h2 className="text-lg font-bold text-slate-800">Promotional banner</h2>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                Site-wide banner shown across the top of every signed-in page (dashboard, analytics, map, etc.) and on the public sign-in page. Stays open until each visitor dismisses it.
              </p>
            </div>
          </div>
          {/* Master on/off toggle. Mirrors the toggle in the form below for
              quick access, but writes nothing until Save. */}
          <label className="inline-flex items-center gap-2 shrink-0 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 accent-emerald-600"
            />
            <span className={`text-sm font-semibold ${enabled ? 'text-emerald-700' : 'text-slate-500'}`}>
              {enabled ? 'Banner ON' : 'Banner OFF'}
            </span>
          </label>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
          </div>
        )}
        {message_ && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <CheckCircle size={16} className="shrink-0 mt-0.5" /> {message_}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 space-y-1.5">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              maxLength={240}
              placeholder="e.g. Founding member offer — save £30/month with code FOUNDING30"
              className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
            />
            <div className="flex justify-between text-[11px] text-slate-400">
              <span>Plain text, no HTML. Keep it short — the banner truncates on small screens.</span>
              <span>{(message || '').length} / 240</span>
            </div>
          </div>

          <div className="md:col-span-2 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Coupon codes (optional)
              </label>
              <button
                type="button"
                onClick={addCouponRow}
                disabled={couponCodes.length >= PROMO_BANNER_MAX_COUPON_CODES}
                className="inline-flex items-center gap-1 text-xs font-medium text-[#043132] hover:text-[#065354] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={14} /> Add code
              </button>
            </div>
            <div className="space-y-2">
              {couponCodes.map((code, index) => (
                <div key={`coupon-${index}`} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => updateCouponAt(index, e.target.value)}
                    placeholder={index === 0 ? 'e.g. FOUNDINGMONTHLY50' : 'e.g. FOUNDINGANNUAL50'}
                    maxLength={40}
                    className="flex-1 text-sm border border-slate-200 rounded-lg p-3 font-mono uppercase tracking-wide focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => removeCouponRow(index)}
                    disabled={couponCodes.length === 1 && !code}
                    className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Remove code"
                    aria-label="Remove coupon code"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400">
              Each code must match a Stripe <em>promotion code</em> (not a coupon ID). Customers click a code on the banner to copy it, then paste on Checkout. Up to {PROMO_BANNER_MAX_COUPON_CODES} codes.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Colour
            </label>
            <select
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none bg-white"
            >
              {PROMO_BANNER_VARIANTS.map((v) => (
                <option key={v} value={v}>
                  {VARIANT_LABELS[v] || v}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Call-to-action label
            </label>
            <input
              type="text"
              value={ctaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
              maxLength={40}
              className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Call-to-action link
            </label>
            <input
              type="text"
              value={ctaUrl}
              onChange={(e) => setCtaUrl(e.target.value)}
              placeholder="/checkout"
              className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
            />
            <p className="text-[11px] text-slate-400">
              Internal route (e.g. <code>/checkout</code>) or full URL (<code>https://…</code>).
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-slate-700">
              Banner version: <span className="font-mono">{version}</span>
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5 max-w-xl">
              Bumping the version re-shows the banner to everyone who'd previously dismissed it. Untick this only when fixing a typo without re-prompting users.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0 mt-1">
            <input
              type="checkbox"
              checked={bumpVersionOnSave}
              onChange={(e) => setBumpVersionOnSave(e.target.checked)}
              className="h-4 w-4 accent-emerald-600"
            />
            <span className="text-xs text-slate-700">Bump version on save</span>
          </label>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-[#043132] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#065354] disabled:opacity-60"
          >
            <Save size={16} /> {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleResetDefaults}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <RotateCcw size={14} /> Reset form to defaults
          </button>
        </div>
      </div>

      {/* Live preview — same DOM the live banner uses, just outside its
          dismiss/snapshot lifecycle so admins always see what they're
          editing regardless of their own dismissal state. */}
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Live preview</p>
        <div className={`relative w-full rounded-lg overflow-hidden ${previewStyles.bar}`}>
          <div className="flex items-center gap-3 px-3 sm:px-5 py-2 sm:py-2.5">
            <Sparkles size={16} className="shrink-0 hidden sm:block" />
            <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs sm:text-sm">
              <span className="font-medium leading-snug">
                {message || <span className="opacity-60 italic">(empty message)</span>}
              </span>
              <PromoCouponChips
                codes={previewCouponCodes}
                chipClassName={previewStyles.chip}
                interactive={false}
              />
            </div>
            <span className={`shrink-0 rounded-md px-3 py-1.5 text-xs sm:text-sm font-semibold ${previewStyles.cta}`}>
              {ctaLabel || DEFAULT_PROMO_BANNER.ctaLabel}
            </span>
            <span className={`shrink-0 p-1 rounded ${previewStyles.dismiss}`}>
              <X size={16} />
            </span>
          </div>
        </div>
        {!enabled && (
          <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
            Banner is currently OFF — preview only. Toggle it on and save to publish.
          </p>
        )}
      </div>
    </div>
  );
}
